import { getEnvMode, isDev, type EnvMode } from '@/utils/env'

const DREAMA_DOMAINS: Record<EnvMode, string> = {
  development: 'https://ciyuan-t.ideaflow.pro',
  test: 'https://test.dreama.fun',
  uat: 'https://uat.dreama.fun',
  production: 'https://dreama.fun',
}

const DREAMA_WEB_APP_DOMAINS: Record<EnvMode, string> = {
  development: 'http://localhost:5173',
  test: 'https://test.dreama.fun',
  uat: 'https://uat.dreama.fun',
  production: 'https://dreama.fun',
}

const SHARED_ROOT_DOMAINS = ['ideaflow.pro', 'dreama.fun'] as const
const SHARED_COOKIE_ROOT_DOMAINS = ['ideaflow.pro'] as const

export function normalizeDomainOrigin(domain: string | null | undefined): string | null {
  const nextDomain = domain?.trim()

  if (!nextDomain) {
    return null
  }

  try {
    return new URL(nextDomain).origin
  } catch {
    return null
  }
}

function getDreamaDomainOverride(mode: EnvMode): string | null {
  if (mode === 'production') {
    return normalizeDomainOrigin(
      import.meta.env.VITE_DREAMA_DOMAIN || import.meta.env.VITE_DREAMA_PRODUCTION_DOMAIN,
    )
  }

  if (mode === 'uat') {
    return normalizeDomainOrigin(
      import.meta.env.VITE_DREAMA_UAT_DOMAIN || import.meta.env.VITE_DREAMA_DOMAIN,
    )
  }

  if (mode === 'test') {
    return normalizeDomainOrigin(
      import.meta.env.VITE_DREAMA_TEST_DOMAIN || import.meta.env.VITE_DREAMA_DOMAIN,
    )
  }

  return normalizeDomainOrigin(
    import.meta.env.VITE_DREAMA_DEVELOPMENT_DOMAIN || import.meta.env.VITE_DREAMA_DOMAIN,
  )
}

function getDreamaWebAppDomainOverride(mode: EnvMode): string | null {
  if (mode === 'production') {
    return normalizeDomainOrigin(
      import.meta.env.VITE_DREAMA_WEB_APP_DOMAIN ||
        import.meta.env.VITE_DREAMA_WEB_APP_PRODUCTION_DOMAIN,
    )
  }

  if (mode === 'uat') {
    return normalizeDomainOrigin(
      import.meta.env.VITE_DREAMA_WEB_APP_UAT_DOMAIN ||
        import.meta.env.VITE_DREAMA_WEB_APP_DOMAIN,
    )
  }

  if (mode === 'test') {
    return normalizeDomainOrigin(
      import.meta.env.VITE_DREAMA_WEB_APP_TEST_DOMAIN ||
        import.meta.env.VITE_DREAMA_WEB_APP_DOMAIN,
    )
  }

  return normalizeDomainOrigin(
    import.meta.env.VITE_DREAMA_WEB_APP_DEVELOPMENT_DOMAIN ||
      import.meta.env.VITE_DREAMA_WEB_APP_DOMAIN,
  )
}

/**
 * 造梦次元h5域名
 */
export function getDreamaDomain(mode: EnvMode = getEnvMode()): string {
  const override = getDreamaDomainOverride(mode)

  if (override) {
    return override
  }

  if (mode === 'development' && isDev()) {
    return (
      normalizeDomainOrigin(import.meta.env.VITE_DREAMA_LOCAL_DOMAIN) || 'http://localhost:8080'
    )
  }

  return DREAMA_DOMAINS[mode]
}

/**
 * 造梦网页版域名。当前官网与 Web App 已统一在 dreama.fun 体系内，
 * Web App 首页为 /feed。
 */
export function getDreamaWebAppDomain(mode: EnvMode = getEnvMode()): string {
  const currentOrigin = getCurrentDreamaWebAppOrigin()

  if (currentOrigin) {
    return currentOrigin
  }

  const override = getDreamaWebAppDomainOverride(mode)

  if (override) {
    return override
  }

  if (mode === 'development' && isDev() && typeof window !== 'undefined') {
    return window.location.origin
  }

  return DREAMA_WEB_APP_DOMAINS[mode]
}

export function getDreamaWebAppUrl(
  fullPath = '/feed',
  mode: EnvMode = getEnvMode(),
): string {
  return new URL(fullPath, getDreamaWebAppDomain(mode)).toString()
}

function getCurrentDreamaWebAppOrigin(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const rootDomain = getSharedRootDomain(window.location.hostname)

  if (rootDomain === 'dreama.fun' || rootDomain === 'ideaflow.pro') {
    return window.location.origin
  }

  return null
}

export function getSharedRootDomain(hostname: string): string | null {
  const normalizedHostname = hostname.trim().toLowerCase().replace(/\.$/, '')

  return (
    SHARED_ROOT_DOMAINS.find(
      (domain) => normalizedHostname === domain || normalizedHostname.endsWith(`.${domain}`),
    ) ?? null
  )
}

export function getSharedCookieDomain(hostname?: string): string | undefined {
  const currentHostname =
    hostname ?? (typeof window === 'undefined' ? '' : window.location.hostname)
  const normalizedHostname = currentHostname.trim().toLowerCase().replace(/\.$/, '')
  const rootDomain = SHARED_COOKIE_ROOT_DOMAINS.find(
    (domain) => normalizedHostname === domain || normalizedHostname.endsWith(`.${domain}`),
  )

  return rootDomain ? `.${rootDomain}` : undefined
}

/**
 * Studio域名
 */
export function getStudioDomain(): string | null {
  const mode = getEnvMode()

  if (mode === 'development') {
    return 'https://studio-t.ideaflow.pro'
  }
  if (mode === 'uat') {
    return null
  }
  if (mode === 'production') {
    return 'https://studio.ideaflow.pro'
  }
  // 默认返回测试域名
  return 'https://studio-t.ideaflow.pro'
}

/**
 * Agent域名
 */
export function getAgentDomain(): string | null {
  const mode = getEnvMode()

  if (mode === 'development') {
    return 'https://agent-t.ideaflow.pro'
  }
  if (mode === 'uat') {
    return null
  }
  if (mode === 'production') {
    return 'https://agent.ideaflow.pro'
  }
  // 默认返回测试域名
  return 'https://agent-t.ideaflow.pro'
}

/**
 * Ideadance域名
 */
export function getIdeadanceDomain(): string | null {
  const mode = getEnvMode()

  if (mode === 'development') {
    return 'https://ideadance-t.ideaflow.pro'
  }
  if (mode === 'uat') {
    return null
  }
  if (mode === 'production') {
    return 'https://ideadance.ideaflow.pro'
  }
  // 默认返回测试域名
  return 'https://ideadance-t.ideaflow.pro'
}
